import { useTranslation } from 'react-i18next';
import Dialog from 'typescript/components/dialog';
import { CompleteOrderError } from 'typescript/constants/enums/checkout';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import {
  onCheckoutPageViewCombined,
  onCheckoutPageClick,
} from 'typescript/utilities/tracking';
import {
  CHECKOUT_PAGE_CLICK_EVENTS,
  COMPONENT_NAMES,
} from 'typescript/constants/tracking';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useEffect } from 'react';

export const COMPLETE_ORDER_ERROR_DIALOG_TEST_ID =
  'complete-order-error-dialog-test-id';

const CompleteOrderErrorDialog = () => {
  const { t } = useTranslation();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { completeOrderError, setCompleteOrderError } =
    useCompleteOrderContext();

  const getBodyText = () => {
    switch (completeOrderError) {
      case CompleteOrderError.PAYMENT: {
        // payment error (e.g. declined card)
        return t('checkout.dialog.errors.body.payment');
      }
      case CompleteOrderError.PRE_AUTH: {
        // pre-auth error, currently means a Forter decline
        return t('checkout.dialog.errors.body.paymentErrorPhoneCartNumber', {
          order_number: orderNumber,
        });
      }
      case CompleteOrderError.CARD_VERIFICATION_REQUIRED: {
        return t('checkout.dialog.errors.body.cardVerificationRequired');
      }
      case CompleteOrderError.CARD_SOURCE_UNUSABLE: {
        // the card source was already used or has expired
        return t('checkout.dialog.errors.body.cardSourceUnusable');
      }
      case CompleteOrderError.CARD_SAVE_FAILED: {
        return t('checkout.dialog.errors.body.cardSaveFailed');
      }
      case CompleteOrderError.SAVED_CARD_NOT_OWNED: {
        return t('checkout.dialog.errors.body.savedCardNotOwned');
      }
      default: {
        // generic complete order error (e.g. network error)
        return t('checkout.dialog.errors.body.generic');
      }
    }
  };

  const getComponentName = () => {
    switch (completeOrderError) {
      case CompleteOrderError.PAYMENT:
      case CompleteOrderError.CARD_VERIFICATION_REQUIRED:
      case CompleteOrderError.CARD_SOURCE_UNUSABLE:
      case CompleteOrderError.CARD_SAVE_FAILED: {
        return COMPONENT_NAMES.APPS.CHECKOUT_V3.COMPLETING_PAYMENT_ERROR;
      }
      case CompleteOrderError.PRE_AUTH: {
        return COMPONENT_NAMES.APPS.CHECKOUT_V3.COMPLETING_PRE_AUTH_ERROR;
      }
      default: {
        return COMPONENT_NAMES.APPS.CHECKOUT_V3.COMPLETING_GENERAL_ERROR;
      }
    }
  };

  useEffect(() => {
    if (!completeOrderError) {
      return;
    }
    onCheckoutPageViewCombined({
      componentName: getComponentName(),
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <Dialog
      dataTestId={COMPLETE_ORDER_ERROR_DIALOG_TEST_ID}
      header={t('checkout.dialog.errors.title')}
      body={getBodyText()}
      showButton
      buttonText={t('checkout.dialog.cta')}
      buttonOnClick={() => {
        onCheckoutPageClick({
          eventDesc:
            CHECKOUT_PAGE_CLICK_EVENTS.completing_order_error_return_to_checkout,
          trackingData,
        });
        setCompleteOrderError(null);
      }}
    />
  );
};

export default CompleteOrderErrorDialog;
